import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./SolarProvider.module.scss";
import solarProviderService from "./../../../httpService/SolarProviderService";
import { GridColumns } from "../../../globals/GridColumns";
import CommonPagination from "../../../common/components/pagination/CommonPagination";
import FilterBy from "../../../common/components/filter-by/FilterBy";
import GridSearchBar from "../../../common/components/grid-search-bar/GridSearchBar";
import { toast } from "react-toastify";
import CommonSelect, { CommonSelectItem } from "../../../common/components/select/CommonSelect";
import CommonDialog from "../../../common/components/dialog/CommonDialog";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import { useForm } from "react-hook-form";
import CommonPhoneInput from "../../../common/components/phone-input/CommonPhoneInput";
import { BounceLoader } from "react-spinners";
import ButtonBadge from "../../../common/components/button-badge/ButtonBadge";
import { GridSolarProviderModel, SolarProviderModel } from "../../../model/provider/provider-dashboard-data";
import SolarProviderDetails from "./solar-provider-details/SolarProviderDetails";
import { STORE_KEY } from "./constant";

type Props = {};

type AddSolarProviderForm = {
	companyName: string;
	email: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
};

const SolarProvider: React.FC<Props> = memo((props: Props) => {
	const filterDictinary: any = useMemo(() => {
		return {
			status: ["Active", "Suspended", "Pending"],
		};
	}, []);

	const [rows, setRows] = useState<GridSolarProviderModel[]>([]);
	const [selectedRow, setSelectedRow] = useState<string | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [pages, setPages] = useState(10);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [filters, setFilters] = useState<any>({});

	const [selectedFilter, setSelectedFilter] = useState(Object.keys(filterDictinary)[0]);
	const [isPopUpOpen, setIsPopUpOpen] = useState(false);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isLoadingPage, setIsLoadingPage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const popupFilterValues = useMemo(() => Object.keys(filterDictinary).map(x => new CommonSelectItem(x, x)), [filterDictinary]);

	const {
		register,
		watch,
		formState: { errors, isValid },
		setValue,
		getValues,
	} = useForm<AddSolarProviderForm>({
		defaultValues: {
			companyName: "",
			email: "",
			firstName: "",
			lastName: "",
			phoneNumber: "",
		},
		reValidateMode: "onSubmit",
		mode: "onTouched",
	});

	useEffect(() => {
		const providerId = localStorage.getItem(STORE_KEY);
		if (providerId) {
			setSelectedRow(providerId);
		}
	}, []);
	useEffect(() => {}, [rows, currentPage, pages]);

	function changeSearch(e: string) {
		setSearch(e);
	}

	useEffect(() => {}, [rows, currentPage, pages]);

	const fetchGridData = useCallback(async () => {
		try {
			setIsLoadingPage(true);
			let requestObj = {
				searchText: debouncedSearch,
				page: currentPage,
				pageSize: 10,
			};

			Object.keys(filters).forEach(x => {
				requestObj = {
					...requestObj,
					[x]: filters[x],
				};
			});

			const response = await solarProviderService.getAllForGrid(requestObj);

			setRows(response.data.items);
			setPages(response.data.totalPages);
			setCurrentPage(response.data.pageIndex);
		} catch (error) {
			toast.error("Error loading Solar Providers");
		} finally {
			setIsLoadingPage(false);
		}
	}, [currentPage, debouncedSearch, filters]);

	useEffect(() => {
		fetchGridData();
	}, [fetchGridData]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 500);

		return () => clearTimeout(timer);
	}, [search]);

	const onRemoveFilter = (filterItem: any) => {
		setFilters((prev: any) => delete prev[filterItem]);
	};

	const setFilter = (selectedFilterValue: string) => {
		setFilters((prev: any) => {
			return {
				...prev,
				[selectedFilter]: selectedFilterValue,
			};
		});
		setIsPopUpOpen(prev => !prev);
	};

	const addNewSolarProvider = async () => {
		try {
			setIsLoading(true);
			await solarProviderService.registerNewSolarProvider(getValues());

			setIsDialogOpen(prev => !prev);
			toast.success("Successfully created new Solar Provider");
		} catch (error) {
			toast.error("Error creating new Solar Provider");
		} finally {
			setIsLoading(false);
		}
	};

	const handleSolarProviderDetails = (row: GridSolarProviderModel) => {
		setSelectedRow(row?.id);
		localStorage.setItem(STORE_KEY, row?.id);
	};

	const deselectProvider = () => {
		localStorage.removeItem(STORE_KEY);
		setSelectedRow(null);
	};

	return (
		<>
			{selectedRow && <SolarProviderDetails onBackPress={deselectProvider} />}
			{!selectedRow && (
				<>
					<div className={`${styles.headerSection} ${styles.standardSectionPadding}`}>
						<div className={styles.text}>Solar Provider Management</div>
						<button className={styles.blackButton} onClick={() => setIsDialogOpen(true)}>
							Add new Solar Provider
						</button>
					</div>
					<div className={`${styles.filterSection}`}>
						<FilterBy filterDictionary={filterDictinary} onPopUpModal={() => setIsPopUpOpen(prev => !prev)} />
						<GridSearchBar placeholder={"Seach by name, email or phone number"} width={"320px"} onChangeHandler={searchText => changeSearch(searchText)} />
					</div>
					{isPopUpOpen && (
						<div className={styles.modalPopup}>
							<div className={styles.modalProperties}>
								{
									<CommonSelect
										items={popupFilterValues}
										selected={popupFilterValues[0]}
										placeholder="Select Filter"
										onChange={item => setSelectedFilter(item?.key)}
										searchable={false}
									/>
								}
								<div className={styles.scrollDropdown}>
									{filterDictinary[selectedFilter].map((x: string) => (
										<div key={x} className={styles.propertyValue} onClick={() => setFilter(x)}>
											{x}
										</div>
									))}
								</div>
							</div>
						</div>
					)}
					{filters && (
						<div className={styles.filterSubSection}>
							{Object.keys(filters).map(x => (
								<div key={x} className={styles.filter}>
									{x}: <div className={styles.blackText}>{filters[x]}</div>
									<svg
										width="20px"
										height="20px"
										viewBox="0 -0.5 25 25"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										style={{ cursor: "pointer" }}
										onClick={() => onRemoveFilter(x)}
									>
										<path
											d="M6.96967 16.4697C6.67678 16.7626 6.67678 17.2374 6.96967 17.5303C7.26256 17.8232 7.73744 17.8232 8.03033 17.5303L6.96967 16.4697ZM13.0303 12.5303C13.3232 12.2374 13.3232 11.7626 13.0303 11.4697C12.7374 11.1768 12.2626 11.1768 11.9697 11.4697L13.0303 12.5303ZM11.9697 11.4697C11.6768 11.7626 11.6768 12.2374 11.9697 12.5303C12.2626 12.8232 12.7374 12.8232 13.0303 12.5303L11.9697 11.4697ZM18.0303 7.53033C18.3232 7.23744 18.3232 6.76256 18.0303 6.46967C17.7374 6.17678 17.2626 6.17678 16.9697 6.46967L18.0303 7.53033ZM13.0303 11.4697C12.7374 11.1768 12.2626 11.1768 11.9697 11.4697C11.6768 11.7626 11.6768 12.2374 11.9697 12.5303L13.0303 11.4697ZM16.9697 17.5303C17.2626 17.8232 17.7374 17.8232 18.0303 17.5303C18.3232 17.2374 18.3232 16.7626 18.0303 16.4697L16.9697 17.5303ZM11.9697 12.5303C12.2626 12.8232 12.7374 12.8232 13.0303 12.5303C13.3232 12.2374 13.3232 11.7626 13.0303 11.4697L11.9697 12.5303ZM8.03033 6.46967C7.73744 6.17678 7.26256 6.17678 6.96967 6.46967C6.67678 6.76256 6.67678 7.23744 6.96967 7.53033L8.03033 6.46967ZM8.03033 17.5303L13.0303 12.5303L11.9697 11.4697L6.96967 16.4697L8.03033 17.5303ZM13.0303 12.5303L18.0303 7.53033L16.9697 6.46967L11.9697 11.4697L13.0303 12.5303ZM11.9697 12.5303L16.9697 17.5303L18.0303 16.4697L13.0303 11.4697L11.9697 12.5303ZM13.0303 11.4697L8.03033 6.46967L6.96967 7.53033L11.9697 12.5303L13.0303 11.4697Z"
											fill="#000000"
										/>
									</svg>
								</div>
							))}
						</div>
					)}
					<div className={styles.gridSection}>
						<table className={styles.grid}>
							<thead>
								<tr>
									{GridColumns.SOLAR_PROVIDER.map(x => (
										<th key={x}>{x}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{rows.map((row, index) => (
									<tr key={row.id} className={`${index % 2 === 0 ? styles.whiteRow : styles.grayRow}`}>
										<td>{row.businessName}</td>
										<td>{row.contactEmail ?? "/"}</td>
										<td>{row.contactPhone ?? "/"}</td>
										<td>{row.numberOfSolarProducts}</td>
										<td>
											{row.status === "Active" && <div className={styles.activeTag}>{row.status}</div>}
											{row.status === "Suspended" && <div className={styles.deactivatedTag}>{row.status}</div>}
											{row.status === "Pending" && <div className={styles.invitedTag}>{row.status}</div>}
										</td>
										<td>
											{/* <ButtonBadge style={styles.viewDetails} text="View Details" /> */}
											<div onClick={() => handleSolarProviderDetails(row)} className={styles.viewDetails}>
												View Details
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className={styles.footerSection}>
						<CommonPagination
							key={pages}
							currentPage={currentPage}
							numberOfPages={pages}
							isLoading={isLoadingPage}
							onPageChange={pageNumber => setCurrentPage(pageNumber)}
						/>
					</div>
					{isDialogOpen && (
						<CommonDialog onCloseDialog={() => setIsDialogOpen(false)}>
							<div className={styles.modalTitle}>
								Create New Solar Provider
								<svg
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									style={{ cursor: "pointer" }}
									onClick={() => setIsDialogOpen(false)}
								>
									<path
										d="M12 12.9436L16.3282 17.2718C16.453 17.3966 16.606 17.4632 16.7872 17.4718C16.9684 17.4804 17.1299 17.4137 17.2718 17.2718C17.4137 17.1299 17.4846 16.9726 17.4846 16.8C17.4846 16.6274 17.4137 16.4701 17.2718 16.3282L12.9436 12L17.2718 7.6718C17.3966 7.54702 17.4632 7.39403 17.4718 7.21283C17.4804 7.03163 17.4137 6.87009 17.2718 6.7282C17.1299 6.58631 16.9726 6.51537 16.8 6.51537C16.6274 6.51537 16.4701 6.58631 16.3282 6.7282L12 11.0564L7.6718 6.7282C7.54702 6.60342 7.39403 6.53676 7.21283 6.5282C7.03163 6.51964 6.87009 6.58631 6.7282 6.7282C6.58631 6.87009 6.51537 7.02736 6.51537 7.2C6.51537 7.37264 6.58631 7.52991 6.7282 7.6718L11.0564 12L6.7282 16.3282C6.60342 16.453 6.53676 16.606 6.5282 16.7872C6.51964 16.9684 6.58631 17.1299 6.7282 17.2718C6.87009 17.4137 7.02736 17.4846 7.2 17.4846C7.37264 17.4846 7.52991 17.4137 7.6718 17.2718L12 12.9436ZM12.0045 24C10.3451 24 8.78496 23.6851 7.32413 23.0554C5.86331 22.4256 4.59259 21.5709 3.51197 20.4913C2.43137 19.4117 1.57589 18.1422 0.945533 16.6827C0.315178 15.2233 0 13.6638 0 12.0045C0 10.3451 0.314878 8.78496 0.944633 7.32413C1.57441 5.86331 2.4291 4.59259 3.5087 3.51197C4.5883 2.43137 5.85782 1.57589 7.31727 0.945534C8.77673 0.315179 10.3362 0 11.9955 0C13.6549 0 15.215 0.314877 16.6759 0.944633C18.1367 1.57441 19.4074 2.4291 20.488 3.5087C21.5686 4.5883 22.4241 5.85782 23.0545 7.31727C23.6848 8.77673 24 10.3362 24 11.9955C24 13.6549 23.6851 15.215 23.0554 16.6759C22.4256 18.1367 21.5709 19.4074 20.4913 20.488C19.4117 21.5686 18.1422 22.4241 16.6827 23.0545C15.2233 23.6848 13.6638 24 12.0045 24ZM12 22.6667C14.9778 22.6667 17.5 21.6333 19.5667 19.5667C21.6333 17.5 22.6667 14.9778 22.6667 12C22.6667 9.02222 21.6333 6.5 19.5667 4.43333C17.5 2.36667 14.9778 1.33333 12 1.33333C9.02222 1.33333 6.5 2.36667 4.43333 4.43333C2.36667 6.5 1.33333 9.02222 1.33333 12C1.33333 14.9778 2.36667 17.5 4.43333 19.5667C6.5 21.6333 9.02222 22.6667 12 22.6667Z"
										fill="#7F7D82"
									/>
								</svg>
							</div>
							<div className={styles.mainContainer}>
								<CommonFormFieldWrapper title="Business Name" required errors={getFormInputErrors(errors.companyName?.message)}>
									<input {...register("companyName", { required: "Business Name is required" })} placeholder="Enter Business Name" type="text" />
								</CommonFormFieldWrapper>
								<CommonFormFieldWrapper title="Email Address" required errors={getFormInputErrors(errors.email?.message)}>
									<input {...register("email", { required: "Email Address is required" })} placeholder="Enter Email Address" type="text" />
								</CommonFormFieldWrapper>
								<CommonFormFieldWrapper title="First Name" required errors={getFormInputErrors(errors.firstName?.message)}>
									<input {...register("firstName", { required: "First Name is required" })} placeholder="First Name" type="text" />
								</CommonFormFieldWrapper>
								<CommonFormFieldWrapper title="Last Name" required errors={getFormInputErrors(errors.lastName?.message)}>
									<input {...register("lastName", { required: "Last Name is required" })} placeholder="Last Name" type="text" />
								</CommonFormFieldWrapper>
								<div className={styles.containerElement}>
									<CommonFormFieldWrapper title="Phone number" required={true} errors={getFormInputErrors(errors.phoneNumber?.message)}>
										<CommonPhoneInput
											registerInputProps={register("phoneNumber", {
												required: "Phone Number is required",
												pattern: {
													value: /^\+(?:[0-9] ?){12}[0-9]$/,
													message: "Invalid phone number",
												},
											})}
											value={watch("phoneNumber") ?? ""}
											defaultCountry="ng"
											onChange={value =>
												setValue("phoneNumber", value, {
													shouldDirty: true,
												})
											}
										/>
									</CommonFormFieldWrapper>
								</div>
							</div>
							<div className={styles.modalButtonSection}>
								<button className={isValid ? styles.btnStyle : styles.btnStyleDisabled} disabled={!isValid} onClick={addNewSolarProvider}>
									{isLoading ? <BounceLoader size={20} color="#DE841B" /> : "Create"}
								</button>
							</div>
						</CommonDialog>
					)}
				</>
			)}
		</>
	);
});

export default SolarProvider;
